import { useEffect, useState } from "react";
import { apiGet, apiSend } from "../api";

// 分类树管理:最多三级。改名会同步已引用模板;有子/有引用模板拒删。
// 递归渲染:每层可以加子(只要没到三级)、改名、删除。

interface Node { id: string; name: string; sortOrder: number; enabled: boolean; children?: Node[] }

export default function Categories() {
  const [tree, setTree] = useState<Node[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = () => apiGet<Node[]>("/categories").then(setTree).catch((e) => setErr(String(e)));
  useEffect(() => { load(); }, []);

  const call = async (fn: () => Promise<unknown>) => {
    setErr(null); setBusy(true);
    try { await fn(); await load(); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };

  const addTop = () => {
    const name = prompt("新一级分类名称?")?.trim();
    if (!name) return;
    call(() => apiSend("POST", "/categories", { parentId: "", name, sortOrder: (tree.length + 1) * 10 }));
  };
  const addChild = (parent: Node, depthAllowed: boolean) => {
    if (!depthAllowed) { alert("已是三级,不能再加子分类"); return; }
    const name = prompt(`在「${parent.name}」下新增子分类:`)?.trim();
    if (!name) return;
    call(() => apiSend("POST", "/categories", {
      parentId: parent.id, name, sortOrder: ((parent.children?.length || 0) + 1) * 10,
    }));
  };
  const rename = (node: Node) => {
    const name = prompt(`重命名「${node.name}」为:`, node.name)?.trim();
    if (!name || name === node.name) return;
    if (!confirm(`确认改名?关联模板会一并同步。`)) return;
    call(() => apiSend("PUT", `/categories/${node.id}`, { name }));
  };
  const remove = (node: Node) => {
    if (!confirm(`确认删除「${node.name}」?(有子分类或已引用模板时会被拒绝)`)) return;
    call(() => apiSend("DELETE", `/categories/${node.id}`));
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-slate-800">分类管理</h1>
          <p className="text-xs text-slate-500 mt-0.5">最多三级。改名同步已关联模板;删除时若下面有子分类或模板会被拒绝。</p>
        </div>
        <button onClick={addTop} disabled={busy} className="w-full sm:w-auto px-3 py-2 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700 disabled:opacity-60">
          + 新增一级分类
        </button>
      </div>
      {err && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded p-2 break-words">{err}</div>}
      {tree.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center text-sm text-slate-400">
          还没有任何分类,点右上「新增一级分类」开始
        </div>
      ) : (
        <div className="space-y-2">
          {tree.map((top) => (
            <TreeNode key={top.id} node={top} depth={1} busy={busy}
              addChild={addChild} rename={rename} remove={remove} />
          ))}
        </div>
      )}
    </div>
  );
}

function TreeNode({ node, depth, busy, addChild, rename, remove }: {
  node: Node; depth: 1 | 2 | 3; busy: boolean;
  addChild: (parent: Node, depthAllowed: boolean) => void;
  rename: (n: Node) => void;
  remove: (n: Node) => void;
}) {
  const canAddChild = depth < 3;
  const children = node.children || [];
  const wrapCls =
    depth === 1 ? "bg-white rounded-xl border border-slate-200" :
    depth === 2 ? "border border-slate-100 rounded-lg bg-slate-50/40" :
                  "";
  const headCls =
    depth === 1 ? "bg-slate-50 rounded-t-xl px-3 sm:px-4 py-3" :
    depth === 2 ? "bg-white rounded-t-lg px-3 py-2" :
                  "px-3 py-2 hover:bg-slate-50";
  const nameCls =
    depth === 1 ? "font-medium text-slate-800 text-sm sm:text-base" :
    depth === 2 ? "text-sm text-slate-700" :
                  "text-sm text-slate-600";

  return (
    <div className={wrapCls}>
      <div className={`flex items-center justify-between flex-wrap gap-2 ${headCls}`}>
        <div className={nameCls}>
          {depth === 3 && <span className="text-slate-300 mr-1">└</span>}
          {node.name}
          {canAddChild && <span className="text-xs text-slate-400 ml-2">({children.length})</span>}
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {canAddChild && (
            <button onClick={() => addChild(node, canAddChild)} disabled={busy}
              className="px-2 py-1 text-xs text-teal-600 hover:bg-teal-50 rounded whitespace-nowrap">+ 子分类</button>
          )}
          <button onClick={() => rename(node)} disabled={busy}
            className="px-2 py-1 text-xs text-slate-600 hover:bg-slate-100 rounded whitespace-nowrap">改名</button>
          <button onClick={() => remove(node)} disabled={busy}
            className="px-2 py-1 text-xs text-rose-500 hover:bg-rose-50 rounded whitespace-nowrap">删除</button>
        </div>
      </div>
      {depth < 3 && (
        <div className={depth === 1 ? "p-2 space-y-1" : "p-1 pl-4 space-y-1"}>
          {children.length === 0 ? (
            <div className="px-3 py-2 text-xs text-slate-400">还没有子分类</div>
          ) : (
            children.map((c) => (
              <TreeNode key={c.id} node={c} depth={(depth + 1) as 2 | 3} busy={busy}
                addChild={addChild} rename={rename} remove={remove} />
            ))
          )}
        </div>
      )}
    </div>
  );
}
